import type { Readable, Writable } from "svelte/store";
import { derived, get, readable, writable } from "svelte/store";
import deepEqual from "fast-deep-equal";
import { AvailabilityStatus } from "@workadventure/messages";
import { AbortError } from "@workadventure/shared-utils/src/Abort/AbortError";
import * as Sentry from "@sentry/svelte";
import { analyticsClient } from "../Administration/AnalyticsClient";
import type { VideoQualitySetting } from "../Connection/LocalUserStore";
import { localUserStore } from "../Connection/LocalUserStore";
import { isIOS, isSafari } from "../WebRtc/DeviceUtils";
import { SoundMeter } from "../Phaser/Components/SoundMeter";
import type { RequestedStatus } from "../Rules/StatusRules/statusRules";
import { statusChanger } from "../Components/ActionBar/AvailabilityStatus/statusChanger";
import {
    type BackgroundConfig,
    type BackgroundTransformer,
    createBackgroundTransformer,
} from "../WebRtc/BackgroundProcessor/createBackgroundTransformer";
import { LL } from "../../i18n/i18n-svelte";
import type { NoiseReducer } from "../WebRtc/AudioProcessor/NoiseReducer";
import { createNoiseReducer } from "../WebRtc/AudioProcessor/NoiseReducer";
import { ManualGainProcessor } from "../WebRtc/AudioProcessor/ManualGainProcessor";
import { MediaStreamConstraintsError } from "./Errors/MediaStreamConstraintsError";
import { BrowserTooOldError } from "./Errors/BrowserTooOldError";
import { errorStore, warningMessageStore } from "./ErrorStore";
import { WebviewOnOldIOS } from "./Errors/WebviewOnOldIOS";

import { createSilentStore } from "./SilentStore";
import { privacyShutdownStore } from "./PrivacyShutdownStore";
import { inExternalServiceStore, myCameraStore, myMicrophoneStore, proximityMeetingStore } from "./MyMediaStore";
import { userMovingStore } from "./GameStore";
import { hideHelpCameraSettings } from "./HelpSettingsStore";
import { isLiveStreamingStore } from "./IsStreamingStore";

import { backgroundConfigStore, backgroundProcessingEnabledStore } from "./BackgroundTransformStore";
import {
    captureNoiseReductionProviderStore,
    noiseReductionStore,
    selectedNoiseReductionProviderStore,
    type NoiseReductionProvider,
} from "./NoiseReductionStore";
import { buildAudioConstraints } from "./AudioCapturePolicy";
import { manualMicrophoneGainStore, microphoneGainStore, selectedMicrophoneGainModeStore } from "./MicrophoneGainStore";
import { EMPTY_LOCAL_STREAM_VALUE, normalizeLocalStreamValue } from "./LocalStreamStoreValueUtils";
import { getLocalStreamForPublishing } from "./LocalStreamPublishingPolicy";

export const inBackgroundSettingsStore = writable<boolean>(false);

export type MediaAccessIssue = "permission_denied" | "no_device";

/**
 * Last camera access failure, or no videoinput reported by the browser.
 * Cleared when the user turns the camera off manually, when a video track is obtained, or when a camera appears.
 */
export const cameraAccessIssueStore = writable<MediaAccessIssue | null>(null);

/**
 * Last microphone access failure, or no audioinput reported by the browser.
 * Cleared when the user turns the microphone off manually, when an audio track is obtained, or when a microphone appears.
 */
export const microphoneAccessIssueStore = writable<MediaAccessIssue | null>(null);

/**
 * A store that contains the camera state requested by the user (on or off).
 */
function createRequestedCameraState() {
    const { subscribe, set } = writable(localUserStore.getRequestedCameraState());

    return {
        subscribe,
        enableWebcam: () => {
            set(true);
            localUserStore.setRequestedCameraState(true);
            cameraAccessIssueStore.set(null);
        },
        disableWebcam: (options?: { retainAccessIssueFromError?: boolean }) => {
            set(false);
            localUserStore.setRequestedCameraState(false);
            if (!options?.retainAccessIssueFromError) {
                cameraAccessIssueStore.set(null);
            }
        },
    };
}

/**
 * A store that contains the microphone state requested by the user (on or off).
 */
function createRequestedMicrophoneState() {
    const { subscribe, set } = writable(localUserStore.getRequestedMicrophoneState());

    return {
        subscribe,
        enableMicrophone: () => {
            set(true);
            localUserStore.setRequestedMicrophoneState(true);
            microphoneAccessIssueStore.set(null);
        },
        disableMicrophone: (options?: { retainAccessIssueFromError?: boolean }) => {
            set(false);
            localUserStore.setRequestedMicrophoneState(false);
            if (!options?.retainAccessIssueFromError) {
                microphoneAccessIssueStore.set(null);
            }
        },
    };
}

/**
 * A store that contains whether the EnableCameraScene is shown or not.
 */
function createEnableCameraSceneVisibilityStore() {
    const { subscribe, set } = writable(false);

    return {
        subscribe,
        showEnableCameraScene: () => set(true),
        hideEnableCameraScene: () => set(false),
    };
}

export const requestedCameraState = createRequestedCameraState();
export const requestedMicrophoneState = createRequestedMicrophoneState();
export const enableCameraSceneVisibilityStore = createEnableCameraSceneVisibilityStore();

/**
 * A store that is true when the megaphone screen is displayed.
 */
export const displayedMegaphoneScreenStore = writable<boolean>(false);

/**
 * GetUserMedia is impacted by a number of stores (proximityMeetingStore, myCameraStore, myMicrophoneStore, inExternalServiceStore, privacyShutdownStore...).
 * Each time a change is done to one of these store, we will make a new GetUserMedia call.
 * If we plan to do many changes at once, we want to call GetUserMedia only once.
 *
 * To do this, you can use this store.
 * Use startBatch() to start a batch of changes (this will disable changes to GetUserMedia), and commitChanges() after the final change to call GetUserMedia.
 */
function createBatchGetUserMediaStore() {
    const { subscribe, set } = writable(false);

    return {
        subscribe,
        startBatch: () => set(true),
        commitChanges: () => set(false),
    };
}

export const batchGetUserMediaStore = createBatchGetUserMediaStore();

/**
 * A store containing whether the webcam was enabled in the last 10 seconds
 */
const enabledWebCam10secondsAgoStore = readable(false, function start(set) {
    let timeout: NodeJS.Timeout | null = null;

    const unsubscribe = requestedCameraState.subscribe((enabled) => {
        if (enabled === true) {
            if (timeout) {
                clearTimeout(timeout);
            }
            timeout = setTimeout(() => {
                set(false);
            }, 10000);
            set(true);
        } else {
            set(false);
        }
    });

    return function stop() {
        if (timeout) {
            clearTimeout(timeout);
        }
        unsubscribe();
    };
});

/**
 * A store containing whether the webcam was enabled in the last 5 seconds
 */
const userMoved5SecondsAgoStore = readable(false, function start(set) {
    let timeout: NodeJS.Timeout | null = null;

    const unsubscribe = userMovingStore.subscribe((moving) => {
        if (moving === true) {
            if (timeout) {
                clearTimeout(timeout);
            }
            set(true);
        } else {
            timeout = setTimeout(() => {
                set(false);
            }, 5000);
        }
    });

    return function stop() {
        unsubscribe();
    };
});

/**
 * A store awaiting the loading of devices information.
 */
export const devicesNotLoaded = writable(true);

const deviceChanged10SecondsAgoStore = readable(false, function start(set) {
    let timeout: NodeJS.Timeout | null = null;

    const unsubscribeCamera = videoConstraintStore.subscribe((constraints) => {
        if (timeout) {
            clearTimeout(timeout);
        }
        set(true);
        timeout = setTimeout(() => {
            set(false);
        }, 10000);
    });

    const unsubscribeAudio = audioConstraintStore.subscribe((constraints) => {
        if (timeout) {
            clearTimeout(timeout);
        }
        set(true);
        timeout = setTimeout(() => {
            set(false);
        }, 10000);
    });

    return function stop() {
        unsubscribeCamera();
        unsubscribeAudio();
    };
});

/**
 * A store containing if the mouse is over the camera button
 */
export const mouseIsHoveringCameraButton = writable(false);

export const cameraNoEnergySavingStore = writable<boolean>(false);

export const streamingMegaphoneStore = writable<boolean>(false);

export const requestedCameraDeviceIdStore: Writable<string | undefined> = writable(
    localUserStore.getPreferredVideoInputDevice() ? localUserStore.getPreferredVideoInputDevice() : undefined
);

export const frameRateStore: Writable<number | undefined> = writable();
export const requestedMicrophoneDeviceIdStore: Writable<string | undefined> = writable(
    localUserStore.getPreferredAudioInputDevice() ? localUserStore.getPreferredAudioInputDevice() : undefined
);

export const usedCameraDeviceIdStore: Writable<string | undefined> = writable();
export const usedMicrophoneDeviceIdStore: Writable<string | undefined> = writable();

export const inOpenWebsite = writable(false);

/**
 * A store that contains video constraints.
 */
export const videoConstraintStore = derived(
    [requestedCameraDeviceIdStore, frameRateStore],
    ([$cameraDeviceIdStore, $frameRateStore]) => {
        const constraints = {
            width: { ideal: 1280 },
            height: { ideal: 720 },
            frameRate: { ideal: 30 },
            facingMode: "user",
            resizeMode: "crop-and-scale",
            aspectRatio: 1.777777778,

            // Uncomment the lines below to simulate a mobile device
            //height: { min: 640, ideal: 1280, max: 1920 },
            //width: { min: 400, ideal: 720, max: 1080 },
            //resizeMode: "none",
        } as MediaTrackConstraints;

        if ($cameraDeviceIdStore !== undefined) {
            constraints.deviceId = {
                exact: $cameraDeviceIdStore,
            };
        }
        if ($frameRateStore !== undefined) {
            constraints.frameRate = { ideal: $frameRateStore };
        }

        return constraints;
    }
);

/**
 * A store that contains audio constraints.
 * Browser mode uses the browser audio-processing stack.
 * Passthrough and RNNoise use a raw microphone capture so the selected reducer
 * is the only thing shaping the noise profile.
 */
export const audioConstraintStore = derived(
    [requestedMicrophoneDeviceIdStore, captureNoiseReductionProviderStore, selectedMicrophoneGainModeStore],
    ([$microphoneDeviceIdStore, $captureNoiseReductionProvider, $selectedMicrophoneGainMode]) => {
        const capturePolicy = buildAudioConstraints(
            $microphoneDeviceIdStore,
            $captureNoiseReductionProvider,
            $selectedMicrophoneGainMode
        );
        logMediaDebug("[capturePolicy] computed", {
            selectedMicrophoneGainMode: $selectedMicrophoneGainMode,
            captureNoiseReductionProvider: $captureNoiseReductionProvider,
            microphoneDeviceId: $microphoneDeviceIdStore,
            status: capturePolicy.status,
            effectiveMicrophoneGainMode: capturePolicy.effectiveMicrophoneGainMode,
            fallbackReason: capturePolicy.fallbackReason,
            constraints: capturePolicy.constraints,
        });
        if (capturePolicy.status === "unavailable" && capturePolicy.fallbackReason) {
            microphoneGainStore.setUnavailable(capturePolicy.fallbackReason, capturePolicy.effectiveMicrophoneGainMode);
        } else {
            microphoneGainStore.setActive(capturePolicy.effectiveMicrophoneGainMode);
        }

        return capturePolicy.constraints;
    }
);

/**
 * A store that contains "true" if the webcam should be stopped for energy efficiency reason - i.e. we are not moving and not in a conversation.
 */
export const cameraEnergySavingStore = derived(
    [
        deviceChanged10SecondsAgoStore,
        userMoved5SecondsAgoStore,
        enabledWebCam10secondsAgoStore,
        mouseIsHoveringCameraButton,
        cameraNoEnergySavingStore,
        devicesNotLoaded,
        isLiveStreamingStore,
        displayedMegaphoneScreenStore,
    ],
    ([
        $deviceChanged10SecondsAgoStore,
        $userMoved5SecondsAgoStore,
        $enabledWebCam10secondsAgoStore,
        $mouseInBottomRight,
        $cameraNoEnergySavingStore,
        $devicesNotLoaded,
        $isLiveStreamingStore,
        $displayedMegaphoneScreenStore,
    ]) => {
        return (
            !$mouseInBottomRight &&
            !$userMoved5SecondsAgoStore &&
            !$deviceChanged10SecondsAgoStore &&
            !$enabledWebCam10secondsAgoStore &&
            !$cameraNoEnergySavingStore &&
            !$devicesNotLoaded &&
            !$isLiveStreamingStore &&
            !$displayedMegaphoneScreenStore
        );
    }
);

export const inJitsiStore = writable(false);
export const inBbbStore = writable(false);
export const isSpeakerStore = writable(false);
export const inLivekitStore = writable(false);
export const isListenerStore = writable(false);
export const listenerWaitingMediaStore = writable<string | undefined>(undefined);
/**
 * When true, the listener has consented to share their camera with the speaker (seeAttendees feature).
 * This store is set to true when the listener accepts the camera sharing popup.
 */
export const listenerSharingCameraStore = writable(false);

export const requestedStatusStore: Writable<RequestedStatus | null> = writable(localUserStore.getRequestedStatus());

export const inCowebsiteZone = derived(
    [inJitsiStore, inBbbStore, inOpenWebsite],
    ([$inJitsiStore, $inBbbStore, $inOpenWebsite]) => {
        return $inJitsiStore || $inBbbStore || $inOpenWebsite;
    },
    false
);

export const silentStore = createSilentStore();

export const availabilityStatusStore = derived(
    [
        inJitsiStore,
        inBbbStore,
        silentStore,
        privacyShutdownStore,
        proximityMeetingStore,
        isSpeakerStore,
        requestedStatusStore,
        inLivekitStore,
        isListenerStore,
    ],
    ([
        $inJitsiStore,
        $inBbbStore,
        $silentStore,
        $privacyShutdownStore,
        $proximityMeetingStore,
        $isSpeakerStore,
        $requestedStatusStore,
        $inLivekitStore,
        $isListenerStore,
    ]) => {
        // Important: Statuses that should not switch to BUSY
        // must be checked BEFORE privacyShutdownStore to prevent switching to BUSY when privacy is enabled.
        if ($inJitsiStore) return AvailabilityStatus.JITSI;
        if ($inBbbStore) return AvailabilityStatus.BBB;
        if (!$proximityMeetingStore) return AvailabilityStatus.DENY_PROXIMITY_MEETING;
        if ($isSpeakerStore) return AvailabilityStatus.SPEAKER;
        if ($silentStore) return AvailabilityStatus.SILENT;
        if ($inLivekitStore) return AvailabilityStatus.LIVEKIT;
        if ($isListenerStore) return AvailabilityStatus.LISTENER;
        if ($requestedStatusStore) return $requestedStatusStore;
        if ($privacyShutdownStore) return AvailabilityStatus.AWAY;

        return AvailabilityStatus.ONLINE;
    },
    AvailabilityStatus.ONLINE
);

// This is a singleton so we can safely not ever unsubscribe from it.
// eslint-disable-next-line svelte/no-ignored-unsubscribe
availabilityStatusStore.subscribe((newStatus: AvailabilityStatus) => {
    try {
        statusChanger.changeStatusTo(newStatus);
    } catch (e) {
        console.error("Error while changing status", e);
        Sentry.captureException(e);
    }
});

let previousComputedVideoConstraint: boolean | MediaTrackConstraints = false;
let previousComputedAudioConstraint: boolean | MediaTrackConstraints = false;

/**
 * A store containing the media constraints we want to apply.
 */
export const mediaStreamConstraintsStore = derived(
    [
        requestedCameraState,
        requestedMicrophoneState,
        myCameraStore,
        myMicrophoneStore,
        inExternalServiceStore,
        enableCameraSceneVisibilityStore,
        videoConstraintStore,
        audioConstraintStore,
        privacyShutdownStore,
        cameraEnergySavingStore,
        availabilityStatusStore,
        batchGetUserMediaStore,
        inBackgroundSettingsStore,
    ],
    (
        [
            $requestedCameraState,
            $requestedMicrophoneState,
            $myCameraStore,
            $myMicrophoneStore,
            $inExternalServiceStore,
            $enableCameraSceneVisibilityStore,
            $videoConstraintStore,
            $audioConstraintStore,
            $privacyShutdownStore,
            $cameraEnergySavingStore,
            $availabilityStatusStore,
            $batchGetUserMediaStore,
            $inBackgroundSettingsStore,
        ],
        set
    ) => {
        // If a batch is in process, don't do anything.
        if ($batchGetUserMediaStore) {
            return;
        }

        let currentVideoConstraint: boolean | MediaTrackConstraints = $videoConstraintStore;
        let currentAudioConstraint: boolean | MediaTrackConstraints = $audioConstraintStore;

        // Shared conditions for disabling media
        const isInExternalService = $inExternalServiceStore === true;
        const isEnergySaving = $cameraEnergySavingStore === true && $enableCameraSceneVisibilityStore === false;
        const isUnavailableStatus =
            $availabilityStatusStore === AvailabilityStatus.DENY_PROXIMITY_MEETING ||
            $availabilityStatusStore === AvailabilityStatus.SILENT ||
            $availabilityStatusStore === AvailabilityStatus.DO_NOT_DISTURB ||
            $availabilityStatusStore === AvailabilityStatus.BACK_IN_A_MOMENT ||
            $availabilityStatusStore === AvailabilityStatus.BUSY;
        const shouldDisableMicrophoneForPrivacy =
            $privacyShutdownStore === true && !localUserStore.getMicrophonePrivacySettings();
        const shouldDisableCameraForPrivacy =
            $privacyShutdownStore === true && !localUserStore.getCameraPrivacySettings();

        // Audio constraints always apply
        if (
            $requestedMicrophoneState === false ||
            $myMicrophoneStore === false ||
            isInExternalService ||
            shouldDisableMicrophoneForPrivacy ||
            isEnergySaving ||
            isUnavailableStatus
        ) {
            currentAudioConstraint = false;
        }

        // Video constraints only apply when NOT in background settings (to allow camera preview)
        if (!$inBackgroundSettingsStore) {
            if (
                $requestedCameraState === false ||
                $myCameraStore === false ||
                isInExternalService ||
                shouldDisableCameraForPrivacy ||
                isEnergySaving ||
                isUnavailableStatus
            ) {
                currentVideoConstraint = false;
            }
        }
        // Let's make the changes only if the new value is different from the old one.
        if (
            !deepEqual(previousComputedVideoConstraint, currentVideoConstraint) ||
            !deepEqual(previousComputedAudioConstraint, currentAudioConstraint)
        ) {
            previousComputedVideoConstraint = currentVideoConstraint;
            previousComputedAudioConstraint = currentAudioConstraint;
            // Let's copy the objects.
            if (typeof previousComputedVideoConstraint !== "boolean") {
                previousComputedVideoConstraint = { ...previousComputedVideoConstraint };
            }
            if (typeof previousComputedAudioConstraint !== "boolean") {
                previousComputedAudioConstraint = { ...previousComputedAudioConstraint };
            }

            set({
                video: currentVideoConstraint,
                audio: currentAudioConstraint,
            });
        }
    },
    {
        video: false,
        audio: false,
    } as {
        video: false | MediaTrackConstraints;
        audio: false | MediaTrackConstraints;
    }
);

export type LocalStreamStoreValue = StreamSuccessValue | StreamErrorValue;

interface StreamSuccessValue {
    type: "success";
    stream: MediaStream | undefined;
}

interface StreamErrorValue {
    type: "error";
    error: Error;
}

let currentStream: MediaStream | undefined = undefined;
let oldConstraints: { video: MediaTrackConstraints | false; audio: MediaTrackConstraints | false } = {
    video: false,
    audio: false,
};
const MEDIA_DEBUG_PREFIX = "[MediaStore][Debug]";
// Use the factory to create the appropriate transformer
let backgroundTransformer: BackgroundTransformer | undefined = undefined;
// Track the last background config to detect if we need to recreate or just update
let lastBackgroundConfig: BackgroundConfig | undefined = undefined;
// AbortController for the in-flight transform; aborted when a new run is scheduled
let currentTransformAbortController: AbortController | null = null;

// Active noise reducer instance (matches the current effective noise reduction provider)
let audioReducer: NoiseReducer | null = null;
let manualGainProcessor: ManualGainProcessor | null = null;
const MANUAL_MICROPHONE_GAIN_FALLBACK_REASON =
    "Manual microphone gain processing failed. Falling back to the default microphone gain behavior.";

/**
 * Update background processor configuration without recreating the transformer
 */
export function updateBackgroundProcessor(config: {
    blurAmount?: number;
    backgroundImage?: string;
    backgroundVideo?: string;
    mode?: string;
    segmenterOptions?: unknown;
}) {
    if (backgroundTransformer && backgroundTransformer.updateConfig) {
        try {
            backgroundTransformer
                .updateConfig({
                    mode: config.mode as "none" | "blur" | "image" | "video",
                    blurAmount: config.blurAmount,
                    backgroundImage: config.backgroundImage,
                    backgroundVideo: config.backgroundVideo,
                })
                .catch((error) => {
                    console.warn("[MediaStore] Failed to update background transformer configuration:", error);
                });

            // Update the tracked config
            if (lastBackgroundConfig && config.mode) {
                lastBackgroundConfig.mode = config.mode as "none" | "blur" | "image" | "video";
            }
            if (lastBackgroundConfig && config.blurAmount !== undefined) {
                lastBackgroundConfig.blurAmount = config.blurAmount;
            }
            if (lastBackgroundConfig && config.backgroundImage !== undefined) {
                lastBackgroundConfig.backgroundImage = config.backgroundImage;
            }
            if (lastBackgroundConfig && config.backgroundVideo !== undefined) {
                lastBackgroundConfig.backgroundVideo = config.backgroundVideo;
            }
        } catch (error) {
            console.warn("[MediaStore] Failed to update background transformer configuration:", error);
        }
    }
}

/**
 * Serializes raw local stream updates so that only one getUserMedia flow runs at a time
 * and the last enqueued update wins (stale completions do not overwrite the store).
 */
let rawStreamGeneration = 0;
let rawStreamUpdateQueue: Promise<void> = Promise.resolve();

type SetRawStreamIfCurrent = (value: LocalStreamStoreValue) => void;

function logMediaDebug(message: string, details?: unknown): void {
    if (details === undefined) {
        console.debug(`${MEDIA_DEBUG_PREFIX} ${message}`);
        return;
    }

    console.debug(`${MEDIA_DEBUG_PREFIX} ${message}`, details);
}

function summarizeConstraint(constraint: false | MediaTrackConstraints): false | Record<string, unknown> {
    if (constraint === false) {
        return false;
    }

    return {
        deviceId: constraint.deviceId,
        noiseSuppression: constraint.noiseSuppression,
        echoCancellation: constraint.echoCancellation,
        autoGainControl: constraint.autoGainControl,
        width: constraint.width,
        height: constraint.height,
        frameRate: constraint.frameRate,
    };
}

function summarizeStream(stream: MediaStream | undefined): Record<string, unknown> {
    if (!stream) {
        return {
            audioTracks: 0,
            videoTracks: 0,
        };
    }

    return {
        audioTracks: stream.getAudioTracks().map((track) => ({
            id: track.id,
            readyState: track.readyState,
            enabled: track.enabled,
            deviceId: track.getSettings().deviceId,
        })),
        videoTracks: stream.getVideoTracks().map((track) => ({
            id: track.id,
            readyState: track.readyState,
            enabled: track.enabled,
            deviceId: track.getSettings().deviceId,
        })),
    };
}

function classifyMediaAccessError(error: unknown): MediaAccessIssue | null {
    const name = error instanceof Error ? error.name : "";
    if (name === "NotAllowedError" || name === "PermissionDeniedError" || name === "SecurityError") {
        return "permission_denied";
    }
    if (name === "NotFoundError" || name === "DevicesNotFoundError") {
        return "no_device";
    }
    return null;
}

async function runRawStreamUpdate(
    constraints: { video: false | MediaTrackConstraints; audio: false | MediaTrackConstraints },
    setIfCurrent: SetRawStreamIfCurrent,
    generation: number
): Promise<{ video: false | MediaTrackConstraints; audio: false | MediaTrackConstraints }> {
    logMediaDebug(`[raw:${generation}] start`, {
        requested: {
            video: summarizeConstraint(constraints.video),
            audio: summarizeConstraint(constraints.audio),
        },
        previous: {
            video: summarizeConstraint(oldConstraints.video),
            audio: summarizeConstraint(oldConstraints.audio),
        },
        currentStream: summarizeStream(currentStream),
    });

    if (navigator.mediaDevices === undefined) {
        if (window.location.protocol === "http:") {
            setIfCurrent({
                type: "error",
                error: new Error("Unable to access your camera or microphone. You need to use a HTTPS connection."),
            });
            return constraints;
        }
        if (isIOS()) {
            setIfCurrent({
                type: "error",
                error: new WebviewOnOldIOS(),
            });
            return constraints;
        }
        setIfCurrent({
            type: "error",
            error: new BrowserTooOldError(),
        });
        return constraints;
    }

    if (currentStream === undefined) {
        setIfCurrent({
            type: "success",
            stream: undefined,
        });
    }

    const nextConstraints = {
        video: constraints.video ?? false,
        audio: constraints.audio ?? false,
    };

    const mustRequestNewVideo =
        (constraints.video && !deepEqual(oldConstraints.video, constraints.video)) ||
        (!currentStream && constraints.video);
    const mustRequestNewAudio =
        (constraints.audio && !deepEqual(oldConstraints.audio, constraints.audio)) ||
        (!currentStream && constraints.audio);

    logMediaDebug(`[raw:${generation}] computed actions`, {
        mustRequestNewVideo,
        mustRequestNewAudio,
        mustStopVideo: oldConstraints.video !== false && constraints.video === false,
        mustStopAudio: oldConstraints.audio !== false && constraints.audio === false,
    });

    if (currentStream) {
        const oldStream = currentStream;
        const mustStopVideo = oldConstraints.video !== false && constraints.video === false;
        const mustStopAudio = oldConstraints.audio !== false && constraints.audio === false;

        if (mustStopVideo) {
            oldStream.getVideoTracks().forEach((t) => {
                t.stop();
                oldStream.removeTrack(t);
            });
        }
        if (mustStopAudio) {
            oldStream.getAudioTracks().forEach((t) => {
                t.stop();
                oldStream.removeTrack(t);
            });
        }
        if (mustStopVideo || mustStopAudio) {
            setIfCurrent({
                type: "success",
                stream: oldStream,
            });
        }
    }

    if (mustRequestNewVideo || mustRequestNewAudio) {
        const newConstraints: MediaStreamConstraints = {};
        if (mustRequestNewVideo) {
            newConstraints.video = constraints.video;
        } else {
            newConstraints.video = false;
        }
        if (mustRequestNewAudio) {
            newConstraints.audio = constraints.audio;
        } else {
            newConstraints.audio = false;
        }

        try {
            logMediaDebug(`[raw:${generation}] getUserMedia request`, {
                constraints: {
                    video: summarizeConstraint(newConstraints.video ?? false),
                    audio: summarizeConstraint(newConstraints.audio ?? false),
                },
            });
            const stream = await navigator.mediaDevices.getUserMedia(newConstraints);
            if (currentStream) {
                const oldStream = currentStream;
                if (oldStream.getVideoTracks().length > 0) {
                    if (stream.getVideoTracks().length > 0) {
                        console.warn("[MediaStore] Replacing video track with newly acquired stream track");
                        oldStream.getVideoTracks().forEach((t) => t.stop());
                    } else {
                        const oldVideoTracks = currentStream.getVideoTracks();
                        oldVideoTracks.forEach((t) => {
                            if (t.readyState !== "ended") {
                                stream.addTrack(t);
                            }
                            currentStream?.removeTrack(t);
                        });
                    }
                }
                if (oldStream.getAudioTracks().length > 0) {
                    if (stream.getAudioTracks().length > 0) {
                        console.warn("[MediaStore] Replacing audio track with newly acquired stream track");
                        oldStream.getAudioTracks().forEach((t) => t.stop());
                    } else {
                        const oldAudioTracks = currentStream.getAudioTracks();
                        oldAudioTracks.forEach((t) => {
                            if (t.readyState !== "ended") {
                                stream.addTrack(t);
                            }
                            currentStream?.removeTrack(t);
                        });
                    }
                }
            }

            currentStream = stream;
            setIfCurrent({
                type: "success",
                stream: currentStream,
            });
            batchGetUserMediaStore.startBatch();
            if (currentStream.getVideoTracks().length > 0) {
                usedCameraDeviceIdStore.set(currentStream.getVideoTracks()[0]?.getSettings().deviceId);
                requestedCameraState.enableWebcam();
            }
            if (currentStream.getAudioTracks().length > 0) {
                usedMicrophoneDeviceIdStore.set(currentStream.getAudioTracks()[0]?.getSettings().deviceId);
                requestedMicrophoneState.enableMicrophone();
            }
            batchGetUserMediaStore.commitChanges();
            hideHelpCameraSettings();
            logMediaDebug(`[raw:${generation}] getUserMedia success`, summarizeStream(currentStream));
        } catch (e) {
            logMediaDebug(`[raw:${generation}] getUserMedia error`, {
                error: e instanceof Error ? { name: e.name, message: e.message } : { message: String(e) },
                classifiedIssue: classifyMediaAccessError(e),
            });
            if (isOverConstrainedError(e) && e.constraint === "deviceId") {
                console.info(
                    "Could not access the requested microphone or webcam. Falling back to default microphone and webcam",
                    newConstraints,
                    e
                );
                batchGetUserMediaStore.startBatch();
                requestedCameraDeviceIdStore.set(undefined);
                requestedMicrophoneDeviceIdStore.set(undefined);
                batchGetUserMediaStore.commitChanges();
            } else if (constraints.video !== false) {
                console.info(
                    "Error. Unable to get microphone and/or camera access. Trying audio only.",
                    newConstraints,
                    e
                );
                setIfCurrent({
                    type: "error",
                    error: e instanceof Error ? e : new Error("An unknown error happened"),
                });
                requestedCameraState.disableWebcam({ retainAccessIssueFromError: true });
                const classified = classifyMediaAccessError(e);
                cameraAccessIssueStore.set(classified);
                if (constraints.audio !== false) {
                    requestedMicrophoneState.disableMicrophone({ retainAccessIssueFromError: true });
                    microphoneAccessIssueStore.set(classified);
                }
            } else if (!constraints.video && !constraints.audio) {
                console.error("Error. getUserMedia called with no audio and no video.");
                setIfCurrent({
                    type: "error",
                    error: new MediaStreamConstraintsError(),
                });
            } else {
                console.info("Error. Unable to get microphone and/or camera access.", newConstraints, e);
                setIfCurrent({
                    type: "error",
                    error: e instanceof Error ? e : new Error("An unknown error happened"),
                });
                if (constraints.audio !== false) {
                    requestedMicrophoneState.disableMicrophone({ retainAccessIssueFromError: true });
                    microphoneAccessIssueStore.set(classifyMediaAccessError(e));
                }
            }
        }
    }

    logMediaDebug(`[raw:${generation}] end`, {
        nextConstraints: {
            video: summarizeConstraint(nextConstraints.video),
            audio: summarizeConstraint(nextConstraints.audio),
        },
        currentStream: summarizeStream(currentStream),
    });
    return nextConstraints;
}

/**
 * A store containing the MediaStream object (or undefined if nothing requested, or Error if an error occurred)
 * This stream includes background transformations when enabled
 *
 * NOTE: We depend on forceTransformerRecreationStore to detect when mode changes require recreation.
 * Parameter changes (blurAmount, etc.) are handled by a separate subscriber to avoid recreating
 * the transformer on every change (which causes WebGL context leaks).
 */
export const rawLocalStreamStore = derived<[typeof mediaStreamConstraintsStore], LocalStreamStoreValue>(
    [mediaStreamConstraintsStore],
    ([$mediaStreamConstraintsStore], set) => {
        const constraints = { ...$mediaStreamConstraintsStore };
        const myGen = ++rawStreamGeneration;
        const setIfCurrent: SetRawStreamIfCurrent = (value) => {
            if (myGen === rawStreamGeneration) {
                set(value);
            }
        };

        rawStreamUpdateQueue = rawStreamUpdateQueue
            .then(async () => {
                const result = await runRawStreamUpdate(constraints, setIfCurrent, myGen);
                if (myGen === rawStreamGeneration) {
                    oldConstraints = result;
                }
            })
            .catch((e) => {
                console.error("Error in raw local stream update queue", e);
                setIfCurrent({
                    type: "error",
                    error: e instanceof Error ? e : new Error("An unknown error happened"),
                });
            });
    },
    EMPTY_LOCAL_STREAM_VALUE
);

/**
 * Serializes local stream (background-transformed) updates so that the last enqueued update wins.
 */
let localStreamGeneration = 0;
let localStreamUpdateQueue: Promise<void> = Promise.resolve();

type SetLocalStreamIfCurrent = (value: LocalStreamStoreValue) => void;

async function runLocalStreamUpdate(
    rawValue: LocalStreamStoreValue | undefined,
    backgroundProcessingEnabled: boolean,
    microphoneGainMode: "provider-default" | "automatic" | "manual",
    noiseProvider: NoiseReductionProvider,
    generation: number,
    setIfCurrent: SetLocalStreamIfCurrent,
    signal: AbortSignal
): Promise<void> {
    const stableRawValue = normalizeLocalStreamValue(rawValue);

    logMediaDebug(`[local:${generation}] start`, {
        noiseProvider,
        microphoneGainMode,
        backgroundProcessingEnabled,
        rawValue:
            stableRawValue.type === "error"
                ? { type: "error", error: { name: stableRawValue.error.name, message: stableRawValue.error.message } }
                : { type: "success", stream: summarizeStream(stableRawValue.stream) },
    });

    if (stableRawValue.type === "error" || stableRawValue.stream === undefined) {
        if (backgroundTransformer) {
            backgroundTransformer.stop();
        }
        teardownAudioReducer();
        teardownManualGainProcessor();
        setIfCurrent(stableRawValue);
        logMediaDebug(`[local:${generation}] early exit`, {
            reason: stableRawValue.type === "error" ? "raw_error" : "missing_stream",
        });
        return;
    }

    // --- Audio noise reduction step ---
    let streamForProcessing = stableRawValue.stream;

    if (noiseProvider === "rnnoise" && stableRawValue.stream.getAudioTracks().length > 0) {
        if (!audioReducer || audioReducer.getProvider() !== noiseProvider) {
            teardownAudioReducer();
            audioReducer = createNoiseReducer("rnnoise");
            noiseReductionStore.setStatus("loading");
            logMediaDebug(`[local:${generation}] initializing RNNoise reducer`);
            try {
                await audioReducer.init();
                logMediaDebug(`[local:${generation}] RNNoise reducer initialized`);
            } catch (err) {
                const isAbort =
                    signal.aborted ||
                    err instanceof AbortError ||
                    (err instanceof DOMException && err.name === "AbortError");
                if (isAbort) {
                    logMediaDebug(`[local:${generation}] RNNoise init cancelled`);
                    return;
                }
                console.warn("[MediaStore][AudioReducer][WARN] RNNoise init failed, falling back to browser", err);
                Sentry.captureException(err);
                teardownAudioReducer();
                noiseReductionStore.setFallback(err instanceof Error ? err.message : String(err));
                logMediaDebug(`[local:${generation}] RNNoise init fallback`, {
                    error: err instanceof Error ? { name: err.name, message: err.message } : { message: String(err) },
                });
            }
        }

        if (audioReducer) {
            try {
                streamForProcessing = await audioReducer.processStream(stableRawValue.stream);
                noiseReductionStore.setActive("rnnoise");
                logMediaDebug(`[local:${generation}] RNNoise processing active`, summarizeStream(streamForProcessing));
            } catch (err) {
                const isAbort =
                    signal.aborted ||
                    err instanceof AbortError ||
                    (err instanceof DOMException && err.name === "AbortError");
                if (isAbort) {
                    logMediaDebug(`[local:${generation}] RNNoise processing cancelled`);
                    return;
                }
                console.warn("[MediaStore][AudioReducer][WARN] RNNoise processStream failed, falling back", err);
                Sentry.captureException(err);
                teardownAudioReducer();
                noiseReductionStore.setFallback(err instanceof Error ? err.message : String(err));
                streamForProcessing = stableRawValue.stream;
                logMediaDebug(`[local:${generation}] RNNoise processing fallback`, {
                    error: err instanceof Error ? { name: err.name, message: err.message } : { message: String(err) },
                    fallbackStream: summarizeStream(streamForProcessing),
                });
            }
        }
    } else if (noiseProvider === "rnnoise") {
        if (audioReducer) {
            teardownAudioReducer();
        }
        noiseReductionStore.setUnavailable("RNNoise requires an active microphone track.");
        logMediaDebug(`[local:${generation}] RNNoise unavailable`, {
            reason: "missing_audio_track",
        });
    } else {
        // Provider is not rnnoise: release any existing reducer
        if (audioReducer) {
            teardownAudioReducer();
        }
        if (noiseProvider !== "rnnoise") {
            noiseReductionStore.setActive(noiseProvider);
        }
        logMediaDebug(`[local:${generation}] using non-RNNoise provider`, {
            provider: noiseProvider,
        });
    }

    // Abort check after async reducer init
    if (signal.aborted) {
        logMediaDebug(`[local:${generation}] aborted after audio stage`);
        return;
    }

    // --- Manual microphone gain step ---
    if (microphoneGainMode === "manual" && streamForProcessing.getAudioTracks().length > 0) {
        const streamBeforeManualGain = streamForProcessing;

        try {
            if (!manualGainProcessor) {
                manualGainProcessor = new ManualGainProcessor();
                logMediaDebug(`[local:${generation}] initializing manual gain processor`);
                await manualGainProcessor.init();
                logMediaDebug(`[local:${generation}] manual gain processor initialized`);
            }

            manualGainProcessor.updateGain(get(manualMicrophoneGainStore));
            streamForProcessing = await manualGainProcessor.processStream(streamForProcessing);
            microphoneGainStore.setActive("manual");
            logMediaDebug(`[local:${generation}] manual gain processing active`, summarizeStream(streamForProcessing));
        } catch (err) {
            const isAbort =
                signal.aborted ||
                err instanceof AbortError ||
                (err instanceof DOMException && err.name === "AbortError");
            if (isAbort) {
                logMediaDebug(`[local:${generation}] manual gain processing cancelled`);
                return;
            }

            console.warn("[MediaStore][ManualGain][WARN] Manual gain processing failed, falling back", err);
            Sentry.captureException(err);
            teardownManualGainProcessor();
            microphoneGainStore.setFallback(MANUAL_MICROPHONE_GAIN_FALLBACK_REASON);
            streamForProcessing = streamBeforeManualGain;
            logMediaDebug(`[local:${generation}] manual gain processing fallback`, {
                error: err instanceof Error ? { name: err.name, message: err.message } : { message: String(err) },
                fallbackStream: summarizeStream(streamForProcessing),
            });
        }
    } else if (manualGainProcessor) {
        teardownManualGainProcessor();
        logMediaDebug(`[local:${generation}] manual gain processor released`, {
            microphoneGainMode,
        });
    }

    if (signal.aborted) {
        logMediaDebug(`[local:${generation}] aborted after manual gain stage`);
        return;
    }

    // --- Video background transform step (unchanged logic) ---
    if (streamForProcessing.getVideoTracks().length === 0 || !backgroundProcessingEnabled) {
        if (backgroundTransformer) {
            backgroundTransformer.stop();
        }
        setIfCurrent({ type: "success", stream: streamForProcessing });
        logMediaDebug(
            `[local:${generation}] publish without background transform`,
            summarizeStream(streamForProcessing)
        );
        return;
    }

    if (!backgroundTransformer) {
        const currentConfig = get(backgroundConfigStore);
        backgroundTransformer = createBackgroundTransformer(currentConfig);
    }

    if (!streamForProcessing || !backgroundTransformer) {
        setIfCurrent({ type: "success", stream: streamForProcessing });
        return;
    }

    try {
        const finalStream = await backgroundTransformer.transform(streamForProcessing, signal);
        lastBackgroundConfig = { ...get(backgroundConfigStore) };
        setIfCurrent({
            type: "success",
            stream: finalStream,
        });
        logMediaDebug(`[local:${generation}] publish with background transform`, summarizeStream(finalStream));
    } catch (error) {
        const isAbort = error instanceof AbortError || (error instanceof DOMException && error.name === "AbortError");
        if (isAbort) {
            logMediaDebug(`[local:${generation}] aborted during background transform`);
            return;
        }
        console.warn("[MediaStore] Failed to transform stream:", error);
        Sentry.captureException(error);
        warningMessageStore.addWarningMessage(get(LL).warning.backgroundProcessing.failedToApply());
        backgroundConfigStore.reset();
        setIfCurrent({
            type: "error",
            error: error instanceof Error ? error : new Error("Background transform failed"),
        });
    }
}

function teardownAudioReducer(): void {
    if (audioReducer) {
        logMediaDebug("[audioReducer] closing reducer", {
            provider: audioReducer.getProvider(),
        });
        try {
            audioReducer.close();
        } catch (err) {
            console.warn("[MediaStore][AudioReducer][WARN] Error closing audio reducer", err);
        }
        audioReducer = null;
    }
}

function teardownManualGainProcessor(): void {
    if (manualGainProcessor) {
        logMediaDebug("[manualGainProcessor] closing processor");
        try {
            manualGainProcessor.close();
        } catch (err) {
            console.warn("[MediaStore][ManualGain][WARN] Error closing manual gain processor", err);
        }
        manualGainProcessor = null;
    }
}

export const localStreamStore = derived<
    [typeof rawLocalStreamStore, typeof backgroundProcessingEnabledStore, typeof selectedMicrophoneGainModeStore],
    LocalStreamStoreValue
>(
    [rawLocalStreamStore, backgroundProcessingEnabledStore, selectedMicrophoneGainModeStore],
    ([$rawLocalStreamStore, $backgroundProcessingEnabled, $selectedMicrophoneGainModeStore], set) => {
        const myGen = ++localStreamGeneration;
        const setIfCurrent: SetLocalStreamIfCurrent = (value) => {
            if (myGen === localStreamGeneration) {
                set(value);
            }
        };

        currentTransformAbortController?.abort(new AbortError("Background transform cancelled: new stream update"));
        const controller = new AbortController();
        currentTransformAbortController = controller;

        localStreamUpdateQueue = localStreamUpdateQueue
            .catch((e) => {
                const isAbort = e instanceof AbortError || (e instanceof DOMException && e.name === "AbortError");
                if (isAbort) {
                    return;
                }
                console.error("Error in local stream update queue", e);
                setIfCurrent({
                    type: "error",
                    error: e instanceof Error ? e : new Error("An unknown error happened"),
                });
            })
            .then(() =>
                runLocalStreamUpdate(
                    $rawLocalStreamStore,
                    $backgroundProcessingEnabled,
                    $selectedMicrophoneGainModeStore,
                    get(captureNoiseReductionProviderStore),
                    myGen,
                    setIfCurrent,
                    controller.signal
                )
            );
    },
    EMPTY_LOCAL_STREAM_VALUE
);

/**
 * When (unavailable status + in background settings), we expose the local stream without video tracks
 * so that LiveKit/WebRTC do not publish camera; preview in BackgroundSettingsPanel still uses localStreamStore.
 */
export const localStreamStoreForPublishing = derived<
    [typeof localStreamStore, typeof availabilityStatusStore, typeof inBackgroundSettingsStore],
    LocalStreamStoreValue
>(
    [localStreamStore, availabilityStatusStore, inBackgroundSettingsStore],
    ([$localStreamStore, $availabilityStatusStore, $inBackgroundSettingsStore], set) => {
        set(getLocalStreamForPublishing($localStreamStore, $availabilityStatusStore, $inBackgroundSettingsStore));
    }
);

/**
 * Firefox does not support the OverconstrainedError class.
 * Instead, it throw an error whose name is "OverconstrainedError"
 */
interface OverconstrainedErrorInterface {
    constraint: string;
}
function isOverConstrainedError(e: unknown): e is OverconstrainedErrorInterface {
    return e instanceof Error && e.name === "OverconstrainedError";
}

function createVolumeStore(streamStore: Readable<LocalStreamStoreValue>) {
    return derived(
        streamStore,
        ($localStreamStoreValue, set) => {
            if ($localStreamStoreValue.type === "error") {
                set(undefined);
                return;
            }
            const mediaStream = $localStreamStoreValue.stream;

            if (mediaStream === undefined || mediaStream.getAudioTracks().length <= 0) {
                set(undefined);
                return;
            }

            const soundMeter = new SoundMeter(mediaStream);
            let error = false;

            const timeout = setInterval(() => {
                try {
                    set(soundMeter.getVolume());
                } catch (err) {
                    if (!error) {
                        console.error(err);
                        error = true;
                    }
                }
            }, 100);

            return () => {
                clearInterval(timeout);
                soundMeter.stop();
            };
        },
        undefined
    );
}

export const localVolumeStore = createVolumeStore(localStreamStore);
export const localMicrophoneHealthVolumeStore = createVolumeStore(rawLocalStreamStore);

// Not unsubscribing is ok, this module is a singleton.
//eslint-disable-next-line svelte/no-ignored-unsubscribe
manualMicrophoneGainStore.subscribe((gain) => {
    manualGainProcessor?.updateGain(gain);
});

let lastTrackedMicrophoneGainState = get(microphoneGainStore);
// Not unsubscribing is ok, this module is a singleton.
//eslint-disable-next-line svelte/no-ignored-unsubscribe
microphoneGainStore.subscribe((state) => {
    const selectedNoiseReductionProvider = get(selectedNoiseReductionProviderStore);
    const hasMeaningfulChange =
        state.mode !== lastTrackedMicrophoneGainState.mode ||
        state.effectiveMode !== lastTrackedMicrophoneGainState.effectiveMode ||
        state.status !== lastTrackedMicrophoneGainState.status ||
        state.fallbackReason !== lastTrackedMicrophoneGainState.fallbackReason;

    if (!hasMeaningfulChange) {
        return;
    }

    logMediaDebug("[microphoneGain] state updated", {
        mode: state.mode,
        effectiveMode: state.effectiveMode,
        status: state.status,
        fallbackReason: state.fallbackReason,
        selectedNoiseReductionProvider,
    });

    const isAutomaticUnavailable =
        state.mode === "automatic" &&
        state.status === "unavailable" &&
        state.fallbackReason !== null &&
        (lastTrackedMicrophoneGainState.status !== "unavailable" ||
            lastTrackedMicrophoneGainState.fallbackReason !== state.fallbackReason ||
            lastTrackedMicrophoneGainState.mode !== "automatic");

    if (isAutomaticUnavailable) {
        analyticsClient.microphoneGainAutomaticUnavailable(state.fallbackReason, selectedNoiseReductionProvider);
    }

    const isManualFallback =
        state.mode === "manual" &&
        state.status === "fallback" &&
        state.fallbackReason !== null &&
        (lastTrackedMicrophoneGainState.status !== "fallback" ||
            lastTrackedMicrophoneGainState.fallbackReason !== state.fallbackReason ||
            lastTrackedMicrophoneGainState.mode !== "manual");

    if (isManualFallback) {
        analyticsClient.microphoneGainManualFallback(
            state.fallbackReason,
            state.effectiveMode,
            selectedNoiseReductionProvider
        );
    }

    lastTrackedMicrophoneGainState = state;
});

const talkIconVolumeThreshold = 10;

export const localVoiceIndicatorStore = derived<Readable<number[] | undefined>, boolean>(
    localVolumeStore,
    ($localVolumeStore) => {
        if ($localVolumeStore === undefined) {
            return false;
        }
        const volume = $localVolumeStore;
        if (volume === undefined) {
            return false;
        }
        const averageVolume = volume.reduce((a, b) => a + b, 0);
        return averageVolume > talkIconVolumeThreshold;
    },
    false
);

/**
 * Device list
 */
export const deviceListStore = readable<MediaDeviceInfo[] | undefined>(undefined, function start(set) {
    let deviceListCanBeQueried = false;

    const queryDeviceList = () => {
        logMediaDebug("[devices] enumerateDevices start");
        // Note: so far, we are ignoring any failures.
        navigator.mediaDevices
            .enumerateDevices()
            .then((mediaDeviceInfos) => {
                // check if the new list has the preferred device
                const preferredVideoInputDevice = localUserStore.getPreferredVideoInputDevice();
                const preferredAudioInputDevice = localUserStore.getPreferredAudioInputDevice();
                const preferredSpeakerDevice = localUserStore.getSpeakerDeviceId();

                if (
                    preferredVideoInputDevice &&
                    mediaDeviceInfos.find((device) => device.deviceId === preferredVideoInputDevice)
                ) {
                    requestedCameraDeviceIdStore.set(preferredVideoInputDevice);
                }
                if (
                    preferredAudioInputDevice &&
                    mediaDeviceInfos.find((device) => device.deviceId === preferredAudioInputDevice)
                ) {
                    requestedMicrophoneDeviceIdStore.set(preferredAudioInputDevice);
                }
                if (
                    preferredSpeakerDevice &&
                    mediaDeviceInfos.find((device) => device.deviceId === preferredSpeakerDevice)
                ) {
                    speakerSelectedStore.set(preferredSpeakerDevice);
                }

                const actualsMediaDevices = get(deviceListStore);
                // get all media that not exist in the list
                if (actualsMediaDevices != undefined) {
                    // set the last new media devices detected
                    const newDevices = mediaDeviceInfos.filter(
                        (device) => actualsMediaDevices.find((d) => d.deviceId === device.deviceId) == undefined
                    );
                    lastNewMediaDeviceDetectedStore.set(newDevices);
                }

                set(mediaDeviceInfos);
                devicesNotLoaded.set(false);
                logMediaDebug("[devices] enumerateDevices success", {
                    count: mediaDeviceInfos.length,
                    audioInputs: mediaDeviceInfos.filter((device) => device.kind === "audioinput").length,
                    videoInputs: mediaDeviceInfos.filter((device) => device.kind === "videoinput").length,
                    audioOutputs: mediaDeviceInfos.filter((device) => device.kind === "audiooutput").length,
                });
            })
            .catch((e) => {
                console.error(e);
                devicesNotLoaded.set(false);
                logMediaDebug("[devices] enumerateDevices error", {
                    error: e instanceof Error ? { name: e.name, message: e.message } : { message: String(e) },
                });
                throw e;
            });
    };

    const handleDeviceChange = () => {
        logMediaDebug("[devices] devicechange event");
        queryDeviceList();
    };

    const unsubscribe = localStreamStore.subscribe((streamResult) => {
        if (streamResult && streamResult.type === "success" && streamResult.stream !== undefined) {
            if (deviceListCanBeQueried === false) {
                queryDeviceList();
                deviceListCanBeQueried = true;
            }
        }
    });

    if (navigator.mediaDevices) {
        navigator.mediaDevices.addEventListener("devicechange", handleDeviceChange);
    }

    return function stop() {
        unsubscribe();
        if (navigator.mediaDevices) {
            navigator.mediaDevices.removeEventListener("devicechange", handleDeviceChange);
        }
    };
});

export const cameraListStore = derived(deviceListStore, ($deviceListStore) => {
    if ($deviceListStore === undefined) {
        return undefined;
    }

    return removeDuplicateDevices($deviceListStore.filter((device) => device.kind === "videoinput"));
});

/**
 * Context for the camera action-bar tooltip when the camera is off: permission denied vs no usable device.
 */
export const cameraButtonHelpContextStore = derived(
    [cameraAccessIssueStore, cameraListStore, devicesNotLoaded],
    ([issue, cameras, notLoaded]) => {
        if (issue === "permission_denied") {
            return "permission" as const;
        }
        if (issue === "no_device") {
            return "no_device" as const;
        }
        if (!notLoaded && cameras !== undefined && cameras.length === 0) {
            return "no_device" as const;
        }
        return null;
    }
);

export const microphoneListStore = derived(deviceListStore, ($deviceListStore) => {
    if ($deviceListStore === undefined) {
        return undefined;
    }

    return removeDuplicateDevices($deviceListStore.filter((device) => device.kind === "audioinput"));
});

/**
 * Context for the microphone action-bar tooltip when the mic is off: permission denied vs no usable device.
 */
export const microphoneButtonHelpContextStore = derived(
    [microphoneAccessIssueStore, microphoneListStore, devicesNotLoaded],
    ([issue, mics, notLoaded]) => {
        console.log("microphoneButtonHelpContextStore", issue, mics, notLoaded);
        if (issue === "permission_denied") {
            return "permission" as const;
        }
        if (issue === "no_device") {
            return "no_device" as const;
        }
        console.log("mics", mics !== undefined && mics.length === 0);
        if (mics !== undefined && mics.length === 0) {
            return "no_device" as const;
        }
        return null;
    }
);

export const speakerListStore = derived(deviceListStore, ($deviceListStore) => {
    if ($deviceListStore === undefined) {
        return undefined;
    }

    // Livekit does not support audio output device selection on Safari
    // Code: https://github.com/livekit/client-sdk-js/blob/dbaf7a9b784114728857a447734bc5d5453345b4/src/room/utils.ts#L144C1-L153C2
    // And it seems there is no plan to support it. Issue: https://github.com/livekit/components-js/issues/1216
    // Because the audio output selector should work in full-mesh WebRTC AND in Livekit, we have to support the same
    // features in both modes. So we disable audio output device selection on Safari here.
    if (isSafari() || isIOS()) {
        return;
    }

    return removeDuplicateDevices($deviceListStore.filter((device) => device.kind === "audiooutput"));
});

export const selectDefaultSpeaker = () => {
    const devices = get(speakerListStore);
    if (devices !== undefined && devices.length > 0) {
        speakerSelectedStore.set(devices[0].deviceId);
    } else {
        speakerSelectedStore.set("");
    }
};

// This is a singleton so no need to unsubscribe
//eslint-disable-next-line svelte/no-ignored-unsubscribe
speakerListStore.subscribe((devices) => {
    if (devices === undefined) {
        return;
    }
    // if the previous speaker used isn`t defined in the list, apply default speaker
    const previousSpeakerId = get(speakerSelectedStore);
    const previousAudioOutputDevice = devices.find((device) => device.deviceId === previousSpeakerId);
    if (previousAudioOutputDevice === undefined) {
        selectDefaultSpeaker();
    }
});

export const speakerSelectedStore = writable<string | undefined>(localUserStore.getSpeakerDeviceId() ?? undefined);

function removeDuplicateDevices(devices: MediaDeviceInfo[]) {
    const uniqueDevices = new Map<string, MediaDeviceInfo>();
    devices.forEach((device) => {
        uniqueDevices.set(device.deviceId, device);
    });
    return Array.from(uniqueDevices.values());
}

function isConstrainDOMStringParameters(param: ConstrainDOMString): param is ConstrainDOMStringParameters {
    return (
        typeof param === "object" &&
        ((param as ConstrainDOMStringParameters).ideal !== undefined ||
            (param as ConstrainDOMStringParameters).exact !== undefined)
    );
}

// TODO: detect the new webcam and automatically switch on it.
// It is ok to not unsubscribe to this store because it is a singleton.
// eslint-disable-next-line svelte/no-ignored-unsubscribe
cameraListStore.subscribe((devices) => {
    // Store not initialized yet
    if (devices === undefined) {
        return;
    }
    // If the selected camera is unplugged, let's remove the constraint on deviceId
    const constraints = get(videoConstraintStore);
    const deviceId = constraints.deviceId;
    if (!deviceId) {
        return;
    }

    // If we cannot find the device ID, let's remove it.
    if (isConstrainDOMStringParameters(deviceId)) {
        if (!devices.find((device) => device.deviceId === deviceId.exact)) {
            requestedCameraDeviceIdStore.set(undefined);
        }
    }
});

// It is ok to not unsubscribe to this store because it is a singleton.
// eslint-disable-next-line svelte/no-ignored-unsubscribe
microphoneListStore.subscribe((devices) => {
    // Store not initialized yet
    if (devices === undefined) {
        return;
    }

    // If the selected camera is unplugged, let's remove the constraint on deviceId
    const constraints = get(audioConstraintStore);
    if (typeof constraints === "boolean") {
        return;
    }
    const deviceId = constraints.deviceId;
    if (!deviceId) {
        return;
    }

    // If we cannot find the device ID, let's remove it.
    if (isConstrainDOMStringParameters(deviceId)) {
        if (!devices.find((device) => device.deviceId === deviceId.exact)) {
            requestedMicrophoneDeviceIdStore.set(undefined);
        }
    }
});

// It is ok to not unsubscribe to this store because it is a singleton.
// eslint-disable-next-line svelte/no-ignored-unsubscribe
localStreamStore.subscribe((streamResult) => {
    if (streamResult && streamResult.type === "error") {
        if (streamResult.error.name === BrowserTooOldError.NAME || streamResult.error.name === WebviewOnOldIOS.NAME) {
            errorStore.addErrorMessage(streamResult.error);
        }
    }
});

// When the stream is initialized, the new sound constraint is recreated and the first speaker is set.
// If the user did not select the new speaker, the first new speaker cannot be selected automatically.
// It is ok to not unsubscribe to this store because it is a singleton.
// // eslint-disable-next-line svelte/no-ignored-unsubscribe
/*speakerSelectedStore.subscribe((speaker) => {
    const oldValue = localUserStore.getSpeakerDeviceId();
    const currentValue = speaker;
    const speakerList = get(speakerListStore);
    const oldDevice =
        oldValue && speakerList
            ? speakerList.find((mediaDeviceInfo) => mediaDeviceInfo.deviceId == oldValue)
            : undefined;
    if (
        oldDevice !== undefined &&
        speakerList !== undefined &&
        currentValue !== oldDevice.deviceId &&
        speakerList.find((value) => value.deviceId == oldValue)
    ) {
        console.warn("speakerSelectedStore.subscribe", oldValue, currentValue, oldDevice.deviceId);
        speakerSelectedStore.set(oldDevice.deviceId);
    }
});*/

function createVideoQualityStore() {
    const { subscribe, set } = writable<VideoQualitySetting>(localUserStore.getVideoQuality());

    return {
        subscribe,
        setQuality: (quality: VideoQualitySetting) => {
            set(quality);
            localUserStore.setVideoQuality(quality);
        },
    };
}

/**
 * A store containing the video quality setting.
 */
export const videoQualityStore = createVideoQualityStore();

export const lastNewMediaDeviceDetectedStore = writable<MediaDeviceInfo[]>([]);

/**
 * Subscribe to background config changes to update the transformer
 * This avoids recreating the entire stream when only parameters change
 */
const backgroundConfigStoreSubscription = backgroundConfigStore.subscribe(($config) => {
    // Skip if no transformer exists yet
    if (!backgroundTransformer || !lastBackgroundConfig) {
        return;
    }

    updateBackgroundProcessor({
        mode: $config.mode,
        blurAmount: $config.blurAmount,
        backgroundImage: $config.backgroundImage,
        backgroundVideo: $config.backgroundVideo,
    });
});
export const unsubscribeBackgroundConfigStoreSubscription = () => {
    backgroundConfigStoreSubscription();
};
